import { useState, useEffect } from 'react';
import { Users, MessageSquare, Clock, TrendingUp, LucideIcon, ArrowLeft } from 'lucide-react';
import { XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart } from 'recharts';
import { useAuth } from '../../context/AuthContext';
import { fetchAssignedTeams } from '../../api/chat';
import { fetchIdeas } from '../../api/idea';

interface Stat {
  icon: LucideIcon;
  iconColor: string;
  iconBg: string;
  label: string;
  value: string;
  valueColor: string;
}

interface ChartData {
  month: string;
  value: number;
}

interface ProgressItem {
  label: string;
  current: number;
  total: number;
  color: string;
  percentage: number;
}

interface Team {
  _id: string;
  teamName: string;
  leader: { username: string; name: string; email: string };
  members: any[];
  status: string;
}

interface Idea {
  _id: string;
  title: string;
  description: string;
  teamId: string;
  status: string;
  createdAt: string;
}

const TODAY = new Date().toLocaleDateString('en-US', {
  weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
});

export function Dashboard() {
  const { token, user } = useAuth();
  const [view, setView] = useState<'dashboard' | 'teams' | 'ideas'>('dashboard');
  const [teams, setTeams] = useState<Team[]>([]);
  const [ideas, setIdeas] = useState<Idea[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingTeams, setLoadingTeams] = useState(false);
  const [loadingIdeas, setLoadingIdeas] = useState(false);

  // Fetch stats data on mount
  useEffect(() => {
    if (!token) return;
    setLoading(true);
    Promise.all([
      fetchAssignedTeams(token).catch(() => [] as Team[]),
      fetchIdeas(token).then((d) => d.items ?? []).catch(() => [] as Idea[]),
    ]).then(([t, i]) => {
      setTeams(t);
      setIdeas(i);
    }).finally(() => setLoading(false));
  }, [token]);

  const reviewedCount = ideas.filter((i) =>
    i.status === 'submitted_final' || i.status === 'selected' || i.status === 'rejected'
  ).length;
  const pendingCount = ideas.filter((i) => i.status === 'under_mentor_review').length;
  const totalIdeas = ideas.length;
  const totalTeams = teams.length;

  const stats: Stat[] = [
    {
      icon: Users,
      iconColor: 'text-[#0058ab]',
      iconBg: 'bg-[rgba(0,88,171,0.06)]',
      label: 'Teams Assigned',
      value: loading ? '—' : String(totalTeams),
      valueColor: 'text-[#0058ab]',
    },
    {
      icon: MessageSquare,
      iconColor: 'text-[#ff9f43]',
      iconBg: 'bg-[rgba(255,159,67,0.06)]',
      label: 'Ideas Reviewed',
      value: loading ? '—' : String(reviewedCount),
      valueColor: 'text-[#ff9f43]',
    },
    {
      icon: Clock,
      iconColor: 'text-[#00d68f]',
      iconBg: 'bg-[rgba(0,214,143,0.06)]',
      label: 'Pending Actions',
      value: loading ? '—' : String(pendingCount),
      valueColor: 'text-[#00d68f]',
    },
  ];

  // Fetch teams detail view
  useEffect(() => {
    if (view !== 'teams' || !token || teams.length > 0) return;
    setLoadingTeams(true);
    fetchAssignedTeams(token)
      .then(setTeams)
      .catch((err) => console.error('Failed to fetch teams:', err))
      .finally(() => setLoadingTeams(false));
  }, [view, token]);

  // Fetch ideas detail view
  useEffect(() => {
    if (view !== 'ideas' || !token || ideas.length > 0) return;
    setLoadingIdeas(true);
    fetchIdeas(token)
      .then((data) => setIdeas(data.items || []))
      .catch((err) => console.error('Failed to fetch ideas:', err))
      .finally(() => setLoadingIdeas(false));
  }, [view, token]);

  const chartData: ChartData[] = [
    { month: 'Jan', value: 15 },
    { month: 'Feb', value: 32 },
    { month: 'Mar', value: 28 },
    { month: 'Apr', value: 45 },
    { month: 'May', value: 52 },
    { month: 'Jun', value: 68 },
  ];

  const progressData: ProgressItem[] = [
    {
      label: 'Teams Assigned',
      current: totalTeams,
      total: Math.max(totalTeams, 5),
      color: 'bg-[#0058ab]',
      percentage: Math.min(Math.round((totalTeams / Math.max(totalTeams, 5)) * 100), 100),
    },
    {
      label: 'Ideas Reviewed',
      current: reviewedCount,
      total: Math.max(totalIdeas, 1),
      color: 'bg-[#ff9f43]',
      percentage: totalIdeas > 0 ? Math.round((reviewedCount / totalIdeas) * 100) : 0,
    },
    {
      label: 'Pending Actions',
      current: pendingCount,
      total: Math.max(totalIdeas, 1),
      color: 'bg-[#00d68f]',
      percentage: totalIdeas > 0 ? Math.round((pendingCount / totalIdeas) * 100) : 0,
    },
  ];

  return (
    <div className="w-full" style={{ minHeight: 0, minWidth: 0 }}>
      <div className="px-4 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-8 space-y-4 sm:space-y-6">
      {view === 'dashboard' && (
        <>
          {/* Welcome Card */}
          <div className="bg-gradient-to-br from-[rgba(240,245,255,0.9)] to-[rgba(250,251,255,0.7)] rounded-lg sm:rounded-2xl p-4 sm:p-6 lg:p-8 border border-[rgba(228,232,242,0.5)] shadow-xl">
            <p className="text-xs font-medium text-[#6e7891] uppercase tracking-wider mb-2">{TODAY}</p>
            <h1 className="text-xl sm:text-2xl lg:text-3xl mb-2">
              <span className="text-[#1a2332]">Welcome, </span>
              <span className="text-[#0b9fd9] font-medium">{user?.name || 'Mentor'}</span>
            </h1>
            <p className="text-[#6e7891] text-sm sm:text-base">Monitor team performance and provide guidance.</p>
          </div>

          {/* Stats Cards - Clickable to show teams/ideas */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 lg:gap-6" style={{ minWidth: 0 }}>
            {stats.map((stat, index) => {
              const Icon = stat.icon;
              const isClickable = index < 2;
              return (
                <div
                  key={index}
                  onClick={() => {
                    if (index === 0) setView('teams');
                    else if (index === 1) setView('ideas');
                  }}
                  className={`bg-gradient-to-br from-white/90 to-white/60 backdrop-blur-sm rounded-lg sm:rounded-2xl p-4 sm:p-5 lg:p-6 border border-[rgba(228,236,244,0.4)] shadow-lg hover:shadow-xl transition-all duration-300 ${isClickable ? 'cursor-pointer' : ''}`}
                  style={{ minWidth: 0 }}
                >
                  <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl ${stat.iconBg} flex items-center justify-center mb-4 sm:mb-6 shadow-md flex-shrink-0`}>
                    <Icon className={`w-5 h-5 sm:w-6 sm:h-6 ${stat.iconColor}`} />
                  </div>
                  <p className="text-xs font-medium text-[#8b93a8] uppercase tracking-wider mb-2">{stat.label}</p>
                  <p className={`text-2xl sm:text-3xl font-semibold ${stat.valueColor}`}>{stat.value}</p>
                </div>
              );
            })}
          </div>

          {/* Progress Overview */}
          <div className="bg-white rounded-lg sm:rounded-2xl p-4 sm:p-6 border border-[#e4e8ee] shadow-lg" style={{ minWidth: 0 }}>
            <div className="flex items-center gap-2 mb-4 sm:mb-6">
              <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 text-[#0058ab] flex-shrink-0" />
              <h2 className="text-base sm:text-lg font-semibold text-[#1a2332]">Progress Overview</h2>
            </div>
            <div className="space-y-4 sm:space-y-5">
              {progressData.map((item, index) => (
                <div key={index} style={{ minWidth: 0 }}>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-xs sm:text-sm font-medium text-[#4a5068]">{item.label}</span>
                    <span className="text-xs sm:text-sm text-[#6e7891] flex-shrink-0">
                      {item.current} / {item.total}
                    </span>
                  </div>
                  <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className={`h-full ${item.color} rounded-full transition-all duration-500`}
                      style={{ width: `${item.percentage}%` }}
                    ></div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Team Performance Trend Chart */}
          <div className="bg-white rounded-lg sm:rounded-2xl p-4 sm:p-6 border border-[#e4e8ee] shadow-lg" style={{ minWidth: 0 }}>
            <h2 className="text-base sm:text-lg font-semibold text-[#1a2332] mb-1">Team Performance Trend</h2>
            <p className="text-xs sm:text-sm text-[#6e7891] mb-4 sm:mb-6">Analytics for the current hackathon cycle.</p>
            <div style={{ width: '100%', height: '300px', minWidth: 0, minHeight: 0 }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData}>
                  <defs>
                    <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#0058ab" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#1db8f2" stopOpacity={0.05} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e4e8ee" vertical={false} />
                  <XAxis 
                    dataKey="month" 
                    stroke="#8b93a8" 
                    fontSize={12}
                    tickLine={false}
                    axisLine={false}
                  />
                  <YAxis 
                    stroke="#8b93a8" 
                    fontSize={12}
                    tickLine={false}
                    axisLine={false}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'rgba(255, 255, 255, 0.95)',
                      border: '1px solid rgba(228, 232, 242, 0.5)',
                      borderRadius: '12px',
                      boxShadow: '0 10px 40px rgba(0, 88, 171, 0.15)',
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="value"
                    stroke="#0058ab"
                    strokeWidth={3}
                    fill="url(#colorValue)"
                    dot={{ fill: '#0058ab', strokeWidth: 2, r: 5 }}
                    activeDot={{ r: 7, fill: '#0058ab' }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        </>
      )}

      {/* Teams View */}
      {view === 'teams' && (
        <div className="px-4 sm:px-6 lg:px-8 py-4 sm:py-6 space-y-4 sm:space-y-6" style={{ minWidth: 0 }}>
          <button
            onClick={() => setView('dashboard')}
            className="inline-flex items-center gap-2 text-[#0058ab] font-medium hover:text-[#003d7a] transition text-sm sm:text-base flex-shrink-0"
          >
            <ArrowLeft className="w-4 h-4 flex-shrink-0" />
            Back to Dashboard
          </button>
          <div className="bg-white rounded-lg sm:rounded-2xl p-4 sm:p-6 border border-[#e4e8ee] shadow-lg" style={{ minWidth: 0 }}>
            <h2 className="text-xl sm:text-2xl font-semibold text-[#1a2332] mb-4 sm:mb-6">Teams Assigned</h2>
            {loadingTeams ? (
              <p className="text-[#6e7891] text-sm">Loading teams...</p>
            ) : teams.length === 0 ? (
              <p className="text-[#6e7891] text-sm">No teams assigned yet.</p>
            ) : (
              <div className="space-y-3 sm:space-y-4" style={{ minWidth: 0 }}>
                {teams.map((team) => (
                  <div key={team._id} className="border border-[#e4e8ee] rounded-lg sm:rounded-xl p-3 sm:p-4 hover:shadow-md transition" style={{ minWidth: 0 }}>
                    <div className="flex flex-col gap-2 sm:gap-3" style={{ minWidth: 0 }}>
                      <div style={{ minWidth: 0 }}>
                        <h3 className="font-semibold text-[#1a2332] text-sm sm:text-base break-words">{team.teamName}</h3>
                        <p className="text-xs sm:text-sm text-[#6e7891]">Leader: {team.leader?.name || 'N/A'}</p>
                      </div>
                      <div className="flex items-center justify-between gap-2" style={{ minWidth: 0 }}>
                        <span className="inline-flex items-center rounded-full bg-[#0058ab]/10 px-3 py-1 text-xs font-medium text-[#0058ab] flex-shrink-0">
                          {team.status || 'Active'}
                        </span>
                        <p className="text-xs sm:text-sm text-[#6e7891] flex-shrink-0">Members: {team.members?.length || 0}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Ideas View */}
      {view === 'ideas' && (
        <div className="px-4 sm:px-6 lg:px-8 py-4 sm:py-6 space-y-4 sm:space-y-6" style={{ minWidth: 0 }}>
          <button
            onClick={() => setView('dashboard')}
            className="inline-flex items-center gap-2 text-[#0058ab] font-medium hover:text-[#003d7a] transition text-sm sm:text-base flex-shrink-0"
          >
            <ArrowLeft className="w-4 h-4 flex-shrink-0" />
            Back to Dashboard
          </button>
          <div className="bg-white rounded-lg sm:rounded-2xl p-4 sm:p-6 border border-[#e4e8ee] shadow-lg" style={{ minWidth: 0 }}>
            <h2 className="text-xl sm:text-2xl font-semibold text-[#1a2332] mb-4 sm:mb-6">Ideas Reviewed</h2>
            {loadingIdeas ? (
              <p className="text-[#6e7891] text-sm">Loading ideas...</p>
            ) : ideas.length === 0 ? (
              <p className="text-[#6e7891] text-sm">No ideas to review yet.</p>
            ) : (
              <div className="space-y-3 sm:space-y-4" style={{ minWidth: 0 }}>
                {ideas.map((idea) => (
                  <div key={idea._id} className="border border-[#e4e8ee] rounded-lg sm:rounded-xl p-3 sm:p-4 hover:shadow-md transition" style={{ minWidth: 0 }}>
                    <div className="flex flex-col gap-2 sm:gap-3" style={{ minWidth: 0 }}>
                      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2" style={{ minWidth: 0 }}>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-[#1a2332] text-sm sm:text-base break-words">{idea.title}</h3>
                          <p className="text-xs sm:text-sm text-[#6e7891] mt-1 line-clamp-2">{idea.description}</p>
                        </div>
                        <span className="inline-flex items-center rounded-full bg-[#ff9f43]/10 px-3 py-1 text-xs font-medium text-[#ff9f43] flex-shrink-0 whitespace-nowrap">
                          {idea.status || 'Draft'}
                        </span>
                      </div>
                      <p className="text-xs text-[#8b93a8]">Created: {new Date(idea.createdAt).toLocaleDateString()}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        )}
      </div>
    </div>
  );
}
